import { DateOnly } from "@/@client/System"
import { CalendarAppointment } from "@/@client/Theasoft_ApiServer_Calendar"
import { getFormattedTimePeriod } from "@/utils/get-formatted-time"
import { DefaultList } from "@components/default-list"
import {
  Box,
  darken,
  Divider,
  Group,
  isLightColor,
  lighten,
  Text,
} from "@mantine/core"
import { ActivityTitleText } from "./ActivitiesCard/activity-title-text"
import { useBackgroundColorForDate } from "./calendar/useColorForDate"
import { useRenderCastTime } from "./renderCastTime"
import { getFormattedDate } from "@/utils/get-formatted-date"
import { colorToHex } from "@/utils/color-to-rgba"
import { LinkAnchor } from "./link-anchor"
import { getStripedGradientRuleFromColor } from "@/utils/getStripedGradientRule"

export function DayEventsList({
  events,
  divider = false,
  todayRef,
}: {
  events: [DateOnly, CalendarAppointment[]][]
  divider?: boolean
  todayRef?: (instance: HTMLDivElement | null) => void
  renderAbsenceInfo?: boolean
  renderEmptyDays?: boolean
}) {
  return (
    <DefaultList
      data={events}
      renderItem={({ entity: item, index }) => (
        <React.Fragment key={index}>
          <RenderDayEvents day={item} divider={divider} todayRef={todayRef} />
          {divider && index < events.length - 1 && <Divider m={0} />}
        </React.Fragment>
      )}
    />
  )
}

function RenderDayEvents({
  day,
  divider,
  todayRef,
}: {
  day: [DateOnly, CalendarAppointment[]]
  divider?: boolean
  todayRef?: (instance: HTMLDivElement | null) => void
}) {
  const color = useBackgroundColorForDate(new Date(day[0]))
  const isToday =
    getFormattedDate(new Date(day[0]), "L") ===
    getFormattedDate(new Date(), "L")
  const ref = isToday ? todayRef : undefined

  return (
    <Box
      ref={ref}
      px={{ base: "xs", lg: "sm" }}
      py={{ base: 0, lg: "xs" }}
      bg={color ? colorToHex(color) : undefined}
    >
      <Group>
        <Text fw={500} w={20}>
          {getFormattedDate(day[0], "ddd")}
        </Text>
        <Text fw={"normal"}>{getFormattedDate(day[0], "DD.MM.")}</Text>
      </Group>
      {day[1].map((event, index) => (
        <CalendarEvent
          key={index}
          event={event}
          divider={divider && index < day[1]?.length - 1}
        />
      ))}
    </Box>
  )
}

function CalendarEvent({
  event,
  divider,
}: {
  event: CalendarAppointment
  divider?: boolean
}) {
  const { renderCastTime } = useRenderCastTime()

  const bg =
    event.blockColorHEX || event.backColorHEX || "var(--mantine-color-dark-3)"

  const bgIsLight = isLightColor(bg, 0)

  const c = bgIsLight ? "black" : "white"

  const bgProps = {
    bg: bg,
    c: c || undefined,
    style: { borderRadius: 5 },
    w: "fit-content",
  }

  return (
    <Box pl={10}>
      <Text {...(event.source === "Duty" && bgProps)}>
        {(!!event.start || !!event.end) &&
          `${getFormattedTimePeriod({
            start: event.start,
            end: event.end,
          })} `}
        {event.source === "Cast" || event.source === "PossibleCast" ? (
          <LinkAnchor
            disabled={!event.id}
            to={"/app/art/activities/$id"}
            params={{ id: event.id!.toString() }}
            search={{ tab: "start" }}
            c={"var(--mantine-color-text)"}
          >
            {event.source === "PossibleCast" && "?"}
            <ActivityTitleText
              type={event.type}
              title={event.title}
              isPublic={event.isPublicEvent}
              location={event.location}
              foreColor={event.foreColorHEX}
              backColor={event.backColorHEX}
            />
          </LinkAnchor>
        ) : (
          <Text
            px={5}
            c={c}
            {...(event.source !== "Duty" && {
              bg: event.isLikeRequest
                ? getStripedGradientRuleFromColor(
                    bg,
                    bgIsLight ? lighten(bg, 0.5) : darken(bg, 0.5)
                  )
                : event.backColorHEX || undefined,
              w: "fit-content",
            })}
            style={{
              borderRadius: 5,
            }}
            span
          >
            {event.title}
            {event.status && ` (${event.status})`}
          </Text>
        )}
        {!!event.description && (
          <Text span c="dimmed">
            {" "}
            - {event.description}
          </Text>
        )}
      </Text>
      {event.additionalCastTimes?.map((castTime, index) => (
        <Text pl={10} key={index}>
          {renderCastTime(castTime)}
        </Text>
      ))}
      {divider && <Divider m={0} variant="dashed" />}
    </Box>
  )
}
